import { getCountryName } from "./format";

function splitAnd(str) {
	if (!str || typeof str !== "string") return [];
	return str
		.split(/\s*AND\s*/i)
		.map((s) => s.trim())
		.filter(Boolean);
}

function yearFrom(publishing_date) {
	const y = String(publishing_date || "").slice(0, 4);
	const n = parseInt(y, 10);
	return Number.isFinite(n) ? n : null;
}

function ageBucket(age) {
	if (age == null || isNaN(age)) return "Unknown";
	if (age < 35) return "<35";
	if (age < 45) return "35-44";
	if (age < 55) return "45-54";
	if (age < 65) return "55-64";
	if (age < 75) return "65-74";
	return "75+";
}

function monthsBucket(m) {
	if (m == null || isNaN(m)) return "Unknown";
	if (m < 3) return "<3m";
	if (m < 6) return "3-6m";
	if (m < 12) return "6-12m";
	return ">12m";
}

export function buildRecords(manuscripts) {
  	const out = [];

	const filterEmpty = (value) => {
		if (value == null || value === undefined) return false;
		if (typeof value === "string") {
			const str = value.trim().toLowerCase();
			return str !== "" && str !== "n/a";
		}
		return true;
	};

	for (const m of manuscripts || []) {
    	const year = yearFrom(m.publishing_date);
    	const country = (m.country || "UNK").trim();
    	const manuscriptId = m.doi || m.title;

    for (const p of m.patients || []) {
		const patientId = p.id;
		const sex = p.sex === 1 ? "Female" : p.sex === 0 ? "Male" : "Unknown";
		const age = Number.isFinite(p.age) ? p.age : null;

		const cancerOrgans = Array.isArray(p.cancer_organs)
			? p.cancer_organs.filter(Boolean)
			: [];
		const cancerOrgan = cancerOrgans.length > 0 ? cancerOrgans : ["Unknown"];

		const cancerType = p.cancer_type || "Unknown";
		const cancerTypeNormalized = p.cancer_type_normalized || "Unknown";

		const metastaticPattern = p.metastatic_pattern || "Unknown";

      	for (const t of p.treatments || []) {
			const assaysRaw = splitAnd(t.molecular_markers_assay);
			const markers = Array.isArray(t.molecular_markers_normalized)
			? t.molecular_markers_normalized.filter(mm => mm?.additional === true)
			: [];

			const therapies = Array.isArray(t.therapies) ? t.therapies : [];
			const drugNames = therapies.map((th) => th?.name).filter(filterEmpty);

			const adverseEffects = Array.isArray(t.adverse_effects)
				? t.adverse_effects
				: [];

			const ae = adverseEffects
				.filter((ae) => ae.discontinuation === false)
				.map((ae) => ae.adverse_effect)
				.filter(filterEmpty);

			const discAE = adverseEffects
				.filter((ae) => ae.discontinuation === true)
				.map((ae) => ae.adverse_effect)
				.filter(filterEmpty);

			const aeGrades = adverseEffects
				.filter((ae) => filterEmpty(ae.adverse_effect))
				.map((ae) => ae.grade)
				.filter((g) => g != null && g !== "")
				.map(String);

			const hasDiscontinuationAE = adverseEffects.some(
				(ae) => ae.discontinuation === true
			);

        	out.push({
				// manuscript
				manuscriptId,
				title: m.title,
				year,
				country,
				// patient
				patientId,
				sex,
				age,
				ageBucket: ageBucket(age),
				cancerOrgan, // Array: ["Pancreas", "Liver"] or ["Unknown"]
				cancerType, // String: "NET G2, pancreatic tail"
				cancerTypeNormalized, // String: "Pancreatic Neuroendocrine Tumor"
				metastaticPattern, // String: "Liver AND Non-regional lymph nodes"
				// treatment facets
				drugType: splitAnd(t.treatment_category),
				drugName: drugNames.length > 0 ? drugNames : ["Unknown"],
				drugCategory: splitAnd(t.treatment_category),
				assay: assaysRaw,

				// Use CAPITALIZED keys (Type, Marker, Measurement) from new database
				biomarkerType: markers
					.map((mm) => mm?.Type || mm?.type)
					.filter(filterEmpty),
				markerGene: markers
					.map((mm) => mm?.Marker || mm?.marker)
					.filter(filterEmpty),
				markerType: markers
					.map((mm) => mm?.Type || mm?.type)
					.filter(filterEmpty),
				measurement: markers
					.map((mm) => mm?.Measurement || mm?.measurement)
					.filter(filterEmpty),

				bestResponse:
					t.best_clinical_response_normalized ||
					t.best_clinical_response ||
					"Unknown",
				clinicalResponse:
					t.best_clinical_response && t.best_clinical_response.trim() !== ""
					? t.best_clinical_response
					: null,
				treatmentType: t.treatment_type || "Unknown",
				timeOnTreatment:
					typeof t.time_on_treatment === "number"
					? t.time_on_treatment
					: null,
				timeOnTreatmentBucket: monthsBucket(t.time_on_treatment),
				reasonDisc: t.reason_for_discontinuing || "Unknown",

				// AE fields with proper extraction
				ae: ae.length > 0 ? ae : [],
				discAE: discAE.length > 0 ? discAE : [],
				aeGrade: aeGrades.length > 0 ? aeGrades : [],

				hasDiscontinuationDueToAE: hasDiscontinuationAE ? "Yes" : "No",
				discAEItems: splitAnd(t.discontinuation_AEs_normalized),
				otherAEItems: splitAnd(t.other_AEs_normalized),
				});
			}
		}
  	}
  	return out;
}


const FIELD_LABELS = {
  other_AEs: "Other Adverse Events",
  AEs_normalized: "Adverse Events",
  discontinuation_AEs: "Discontinuation Adverse Events",
  ae_grade: "Adverse Event Grade",
};

export function humanizeKey(key) {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  return key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export	function isUrl(val) {
	return typeof val === "string" && /^https?:\/\//.test(val);
}

//json summary normalization
function filterNA(val) {
    if (val === null || val === undefined) return null;
    if (typeof val === "string" && val.trim().toLowerCase() === "n/a") return null;
    return val;
}

function transformMarker(mm) {
    if (!mm) return null;
    return {
        biomarker:  mm.biomarker  ?? null,
        marker:     mm.Marker     || mm.marker     || null,
        detail:     mm.Detail     || mm.detail     || null,
        type:       mm.Type       || mm.type       || null,
        origin:     mm.Origin     || mm.origin     || null,
        logic:      mm.Logic      || mm.logic      || null,
    };
}

function transformAE(ae) {
    if (!ae) return null;
    const disc = ae.discontinuation;
    const discontinued =
        disc === true || disc === "Yes" || disc === "yes" ? "Yes" : "No";
    return {
        event:          ae.ae    || null,
        grade:          ae.grade ?? null,
        discontinued,
    };
}

function transformTreatment(t) {
    if (!t) return null;
    return {
        // keep is_MPO for the collapsible header logic in ObjectCard
        is_MPO: t.is_MPO,

        precision_oncology_reason:      filterNA(t.is_MPO_reason),
        biomarkers:                     filterNA(t.molecular_markers),
        additional_molecular_findings:  filterNA(t.additional_molecular_findings),
        molecular_markers_assay:        filterNA(t.molecular_markers_assay),

		
        treatment:                          filterNA(t.treatment),
        treatment_normalized:               filterNA(t.treatment_details),
        treatment_class:                    filterNA(t.treatment_class),
        best_clinical_response:             filterNA(t.best_clinical_response_normalized),
        response_comment:                   filterNA(t.response_comment),
        time_on_treatment:                  t.time_on_treatment ?? null,
        treatment_status:                   filterNA(t.treatment_status),
        reason_for_discontinuing_treatment: filterNA(t.reason_for_discontinuing_treatment),
        discontinuation_AEs:                filterNA(t.discontinuation_AEs),
        other_AEs:                          filterNA(t.other_AEs),
		previous_treatment_normalized: filterNA(t.previous_treatment_normalized),
		comments:                      filterNA(t.comments),
		// foldable marker boxes
        molecular_markers_normalized: Array.isArray(t.molecular_markers_normalized)
            ? t.molecular_markers_normalized.map(transformMarker).filter(Boolean)
            : [],

		AEs_normalized: Array.isArray(t.AEs_normalized)
			? t.AEs_normalized
				.map(transformAE)
				.filter(Boolean)
			: [],
    };
}

function transformPatient(p) {
    if (!p) return null;
    return {
        age:                        p.age          ?? null,
        sex:                        p.sex          || null,
        cancer_type:                p.cancer_type  || null,
        cancer_type_normalized:     p.cancer_type_normalized   || null,
        primary_site:               p.cancer_organ_normalized  || null,
        metastatic_pattern:         filterNA(p.metastatic_pattern),
        clinical_details:           filterNA(p.clinical_details),
        clinical_details_normalized: filterNA(p.clinical_details_normalized),
        treatments: Array.isArray(p.treatments)
            ? p.treatments.map(transformTreatment).filter(Boolean)
            : [],
    };
}

export function transformManuscript(m) {
    if (!m) return null;
    const src = m.json_summary || m; // ← use json_summary as the source

    return {
        title:            src.title    || null,
        doi:              src.doi      || null,
        authors:          src.authors  || null,
        journal:          src.journal  || null,
        country:          getCountryName(src.country)  || null,
        publication_year: m.publishing_date || m.publication_year || src.publishing_date || src.publication_year || null,
        summary:          src.summary  || null,
        patients: Array.isArray(src.patients)
            ? src.patients.map(transformPatient).filter(Boolean)
            : [],
    };
}
